<template>
  <div class="p-5 flex flex-col">
    <p class="font-semibold text-3xl">
      {{ this.$route.meta.title }}
    </p>
    <div class="p-5 flex flex-col mt-5 bg-white rounded-md">
      <table-top
        is-search-has-border
        :page-size-start="false"
        class="mt-6"
        @fetch="fetchAgencies"
      >
        <template #start>
          <vs-button
            v-show="isUserCanAccessMenu(permission.name, 'add')"
            class="button shadow-primary font-semibold"
            color="primary"
            @click="onHandleAdd"
            :disabled="isBusy"
          >
            Add New Agency
          </vs-button>
          <vs-button
            class="button shadow-secondary font-semibold ml-5"
            color="secondary"
            @click="isDownload = true"
            :disabled="isDownload"
          >
            Export
          </vs-button>
        </template>
      </table-top>
      <data-table
        class="mt-6"
        :table-header="header"
        :table-data="agencies"
        :sort-by="filters.sort_by"
        :sort-dir="filters.sort_dir"
        body-height="auto"
        :loading="isBusy"
        @sort="sort"
      >
        <template #no="{ index }">
          <div class="flex flex-row w-min">
            {{ (currentPage - 1) * pageSize + index + 1 }}
          </div>
        </template>
        <template #action="{ row }">
          <div class="flex flex-row gap-x-3">
            <template v-if="row.isEditing">
              <div
                :class="`flex items-center justify-center icon-transparent-bg complete ${isProcessing && 'animate-bounce opacity-50 cursor-not-allowed'}`"
                @click="isProcessing ? null : onHandleSubmit(row)"
              >
                <img
                  :src="require('@/assets/shipo-icon-check-circle.svg')"
                  alt="save"
                >
              </div>
              <div
                :class="`flex items-center justify-center icon-transparent-bg delete ${isProcessing && 'animate-bounce opacity-50 cursor-not-allowed'}`"
                @click="isProcessing ? null : onHandleCancel(row)"
              >
                <img
                  :src="require('@/assets/shipo-icon-close-circle.svg')"
                  alt="cancel"
                >
              </div>
            </template>
            <template v-else>
              <div
                v-show="isUserCanAccessMenu(permission.name, 'edit')"
                class="flex items-center justify-center icon-transparent-bg"
                @click="onHandleEdit(row)"
              >
                <img
                  :src="require('@/assets/shipo-icon-edit.svg')"
                  alt="save"
                >
              </div>
              <div
                v-show="isUserCanAccessMenu(permission.name, 'delete')"
                class="flex items-center justify-center icon-transparent-bg"
                @click="onHandleDelete(row)"
              >
                <img
                  :src="require('@/assets/shipo-icon-delete.svg')"
                  alt="cancel"
                >
              </div>
            </template>
          </div>
        </template>
        <template #name="{ row }">
          <div v-if="row.isEditing">
            <vs-input
              :disabled="isProcessing"
              :danger="!!!row.editForm.name || validate === 'name'"
              :danger-text="validate ? 'Name already exist' : '* Required'"
              v-model="row.editForm.name"
              placeholder="Enter agency name"
            />
          </div>
          <div v-else>
            {{ row.name || '-' }}
          </div>
        </template>
        <template #code="{ row }">
          <div v-if="row.isEditing">
            <vs-input
              :disabled="isProcessing"
              :danger="!!!row.editForm.code || validate === 'code'"
              :danger-text="validate ? 'Code already exist' : '* Required'"
              v-model="row.editForm.code"
              placeholder="Enter agency code"
            />
          </div>
          <div v-else>
            {{ row.code || '-' }}
          </div>
        </template>
        <template #active="{ row }">
          <vs-switch
            :disabled="isProcessing"
            v-if="row.isEditing"
            v-model="row.editForm.active"
            color="secondary"
          />
          <div
            v-else
            :class="`badge-status ${row.active ? 'active' : 'inactive'}`"
          >
            {{ row.active ? 'Active' : 'Inactive' }}
          </div>
        </template>
        <template #created_by="{ row }">
          <div>
            {{ row.isEditing ? '-' : row.created_by }}
          </div>
        </template>
        <template #created_at="{ row }">
          <div>
            {{ row.isEditing ? '-' : moment(row.created_at).format('DD MMM YYYY HH:mm') }}
          </div>
        </template>
      </data-table>
      <table-bottom
        class="mt-6"
        @fetch="fetchAgencies"
      />
    </div>
    <pop-up
      :active.sync="popUp.isShown"
      is-warning
      title="Delete Agency"
      left-button="Cancel"
      right-button="Yes, Delete"
      :is-busy="isProcessing"
      @close="popUp = { isShown: false, isShownRestore: false, tempId: null, tempName: '' }"
      @leftButton="popUp = { isShown: false, isShownRestore: false, tempId: null, tempName: '' }"
      @rightButton="deleteAgencyManagement"
    >
      <p class="text-center mt-1">
        Are you sure want to delete
      </p>
      <p class="text-center">
        "{{ popUp.tempName }}"?
      </p>
      <p class="text-center">
        This action cannot be undone
      </p>
    </pop-up>
    <pop-up
      :active.sync="popUpSuccess"
      @close="resetTableFilter"
      @ok="resetTableFilter"
      title="Success"
    >
      Agency successfully deleted.
    </pop-up>
    <pop-up
      :active.sync="popUp.isShownRestore"
      is-restore
      is-warning
      title="Restore Agency"
      left-button="Cancel"
      right-button="Restore"
      :is-busy="isProcessing"
      @close="popUp = { isShown: false, isShownRestore: false, tempId: null, tempName: '' }"
      @leftButton="popUp = { isShown: false, isShownRestore: false, tempId: null, tempName: '' }"
      @rightButton="execute"
    >
      <p class="text-center mt-1">
        “Agency {{ popUp.tempName }}” already exists but deleted.
      </p>
      <p class="text-center">
        Would you like to restore?
      </p>
    </pop-up>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import DataTable from '@/components/dataTable'
import PopUp from '@/components/popUp'
import TableBottom from '@/components/tableBottom'
import TableTop from '@/components/tableTop'
import { PAGE_TITLE } from '@/fixtures/Constants'

import downloadUtils from '../../../utils/downloadUtils'

export default {
  metaInfo() {
    return {
      title: `${PAGE_TITLE} ${this.$route.meta.title}`
    }
  },
  components: {
    DataTable,
    TableBottom,
    TableTop,
    PopUp
  },
  data: () => ({
    header: [
      { key: 'no', label: 'No', sortable: false, width: '50px' },
      { key: 'name', label: 'Agency Name', sortable: true },
      { key: 'code', label: 'Code', sortable: true },
      { key: 'active', label: 'Status', sortable: true },
      { key: 'created_by', label: 'Created By', sortable: true },
      { key: 'created_at', label: 'Created Time', sortable: true }
    ],
    filters: {
      sort_by: 'name',
      sort_dir: 'asc',
      is_active: null
    },
    popUp: {
      isShown: false,
      isShownRestore: false,
      tempId: null,
      tempName: ''
    },
    popUpSuccess: false,
    isAdding: false,
    restoreForm: {
      restore: false
    },
    isBusy: false,
    agencies: [],
    validate: '',
    isDownload: false,
    isProcessing: false
  }),
  computed: {
    ...mapGetters({
      agencyManagements: 'AgencyManagement/agencies',
      currentPage: 'Table/currentPage',
      pageSize: 'Table/pageSize',
      agenciesFiles: 'AgencyManagement/agenciesFiles'
    }),
    permission() {
      return { page: this.$route.meta.permission.page, name: this.$route.meta.permission.name }
    },
    isActionColumnVisible() {
      return this.isUserCanAccessMenu(this.permission.name, 'edit') || this.isUserCanAccessMenu(this.permission.name, 'delete')
    }
  },
  watch: {
    agencyManagements: {
      handler(val) {
        if (val?.data) {
          this.agencies = val.data.map(item => ({
            ...item,
            isEditing: false,
            editForm: null
          }))
        }
      }
    },
    isDownload: {
      handler(val) {
        if (!!val) {
          this.downloadAgencies();
        }
      }
    },
    agenciesFiles: {
      handler(val) {
        if (!this.isEmpty(val)) {
          downloadUtils(val, `Agencies.xlsx`)
          // Reset the export file state
          this.$store.dispatch('AgencyManagement/resetAgenciesFiles');
        }
      }
    },
    isActionColumnVisible: {
      immediate: true,
      handler(val) {
        const index = this.header.findIndex(header => header.key === 'action');
        if (val && index === -1) {
          this.header.splice(1, 0, { key: 'action', label: 'Action', sortable: false, width: '75px' });
        } else if (!val && index !== -1) {
          this.header.splice(index, 1);
        }
      }
    }
  },
  mounted() {
    this.fetchAgencies()
  },
  methods: {
    async fetchAgencies() {
      try {
        this.isBusy = true
        const apiFilters = {
          is_master_data: true,
          sort_dir: this.filters.sort_dir || 'asc',
          sort_by: this.filters.sort_by || 'name',
          page: this.currentPage || 1,
          limit: this.pageSize || 10
        };

        await this.$store.dispatch('AgencyManagement/fetchAgencies', apiFilters)

        this.isAdding = false
        this.isBusy = false
      } catch (e) {
        this.isBusy = false
        this.showFailedNotification(this, 'Failed to fetch Agency Management list, please try again')
      }
    },
    sort(sortBy, sortDir) {
      this.filters.sort_by = sortBy;
      this.filters.sort_dir = sortDir;
      this.fetchAgencies();
    },
    async deleteAgencyManagement() {
      if (this.isAdding || this.agencies.some(r => r.isEditing)) {
        this.showFailedNotification(this, 'Only one action can be active at a time. Please save your changes first', 'Error')
        return
      }
      this.isProcessing = true;
      try {
        await this.$store.dispatch('AgencyManagement/deleteAgency', this.popUp.tempId)

        this.popUp = { isShown: false, isShownRestore: false, tempId: null, tempName: '' }
        this.isProcessing = false;
        this.popUpSuccess = true
      } catch (e) {
        this.isProcessing = false;
        this.showFailedNotification(this, 'Failed to delete Agency Management, please try again')
      }
    },
    async resetTableFilter() {
      await this.$store.dispatch('Table/resetConfig')
      await this.fetchAgencies()
      this.popUpSuccess = false
    },
    onHandleAdd() {
      // Validate if have active action
      if (this.isAdding || this.agencies.some(row => row.isEditing)) {
        this.showFailedNotification(this, 'Only one action can be active at a time. Please save your changes first', 'Error')
        return
      }

      // Create new row
      const newRow = {
        id: `new-${  Date.now()}`, // Temporary ID for new row
        isEditing: true,
        editForm: {
          name: '',
          code: '',
          active: true
        }
      }

      this.isAdding = true
      this.agencies.unshift(newRow)
    },
    onHandleCancel(row) {
      if (this.isAdding) {
        // Remove the new row if we're canceling an add
        const index = this.agencies.findIndex(item => item.id === row.id)
        if (index !== -1) {
          this.agencies.splice(index, 1)
        }
      } else {
        // Just cancel the edit
        const index = this.agencies.findIndex(item => item.id === row.id)
        if (index !== -1) {
          this.agencies[index].isEditing = false
          this.$set(this.agencies[index], 'isEditing', false)
        }
      }
      this.isAdding = false
    },
    async onHandleSubmit(row) {
      // Validate fields
      const { editForm } = row
      const validate = Object.keys(editForm).find(key => editForm[key] === '')

      if (validate) {
        this.showFailedNotification(this, `Agency ${validate} cannot be empty`, 'Error')
        return
      }
      this.isProcessing = true;
      try {
        const { isEditing, ...dataForm } = row

        await this.checkRestoreData(dataForm.editForm)

        if (this.popUp.isShownRestore) return

        await this.execute(dataForm)
        this.isProcessing = false;
      } catch ({ error }) {
        this.isProcessing = false;
        let message = error?.errors?.[0]?.[0]?.message || error?.message || 'Failed to save changes, please try again'

        // set validate state & error message for value that already exist
        if (error?.message?.includes('already')) {
          if (error?.message?.includes('name')) {
            this.validate = 'name'
            message = 'Agency Name already exists'
          }
          if (error?.message?.includes('code')) {
            this.validate = 'code'
            message = 'Agency Code already exists'
          }
        }

        this.showFailedNotification(this, message, 'Error')
      }
    },
    onHandleDelete(row) {
      if (this.isAdding || this.agencies.some(row => row.isEditing)) {
        this.showFailedNotification(this, 'Only one action can be active at a time. Please save your changes first', 'Error')
        return
      }

      this.popUp = { isShown: true, isShownRestore: false, tempId: row.id, tempName: row.name }
    },
    async checkRestoreData(dataForm) {
      const data = await this.$store.dispatch('AgencyManagement/getAgencyManagementByName', dataForm.name)

      // Populate restore data and show popup restore
      if (data?.is_deleted) {
        this.popUp = {
          isShown: false,
          isShownRestore: true,
          tempId: data.id,
          tempName: dataForm.name
        }

        this.restoreForm = {
          ...dataForm,
          id: data.id,
          restore: true
        }
      }
    },
    async execute(form) {
      this.isProcessing = true;
      if (this.isAdding && !this.restoreForm?.restore ) {
        // Handle add
        await this.$store.dispatch('AgencyManagement/postAgency', form.editForm)
        this.showSuccessNotification(this, 'Agency successfully added.')
      } else {
        // Handle edit
        // Populate data restore or data edit
        const { id, ...editForm } = this.restoreForm?.restore ? this.restoreForm : form.editForm
        await this.$store.dispatch('AgencyManagement/putAgency', {
          id: id,
          form: editForm
        })
        this.showSuccessNotification(
          this,
          `Agency successfully ${
            this.restoreForm?.restore
              ? 'restored.' : 'updated.'
          }`
        )
      }

      // Close popup, cancel edit and refetch list
      this.popUp = this.$options.data().popUp;
      this.cancelAllEdits()
      this.fetchAgencies()
      this.isProcessing = false;
    },
    cancelAllEdits() {
      // Remove Index 1 For isAdding
      if (this.agencies.length > 10) {
        this.agencies.splice(0, 1)
      }

      // Cancel any existing edit
      this.agencies.map(row => {
        if (row.isEditing) {
          this.$set(row, 'isEditing', false)
        }
      })
      this.isAdding = false
    },
    onHandleEdit(row) {
      // Cancel any existing edit or add
      if (this.isAdding || this.agencies.some(r => r.isEditing)) {
        this.showFailedNotification(this, 'Only one action can be active at a time. Please save your changes first', 'Error')
        return
      }

      // Populate Edit Form
      const index = this.agencies.findIndex(item => item.id === row.id)
      if (index !== -1) {
        this.$set(this.agencies[index], 'isEditing', true)
        this.$set(this.agencies[index], 'editForm', {
          id: row.id,
          name: row.name,
          code: row.code,
          active: row.active,
          restore: false
        })
      }
    },
    async downloadAgencies() {
      try {
        this.isBusy = true
        await this.$store.dispatch('AgencyManagement/downloadAgencies', {
          is_master_data: true,
          sort_dir: this.filters.sort_dir || 'asc',
          sort_by: this.filters.sort_by || 'name'
        }, {
          headers: {
            accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          },
          responseType: 'blob'
        });
        this.isBusy = false
        this.isDownload = false;
      } catch (error) {
        this.isDownload = false;
        this.isBusy = false
        this.showFailedNotification(this, 'Failed to download agencies, please try again');
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.icon-transparent-bg {
  @apply cursor-pointer;
  @apply min-w-8;
  @apply h-8;
  @apply bg-primary;
  border-radius: 0.5rem;

  &.complete {
    background-color: #D1F9E3;
  }

  &.delete {
    background-color: rgba(255, 99, 123, 0.16);
  }
}

.badge-status {
  @apply w-20;
  text-align: center;
  padding: 5px 0;
  border-radius: 5px;

  &.active {
    @apply bg-green-200 text-green-700 border-green-400
  }

  &.inactive {
    @apply bg-red-200 text-red-700 border-red-400
  }
}
</style>
